<script setup lang="ts">
import { computed } from 'vue';
import BaseButton from './BaseButton.vue';
import { formatPrice } from '@/utils/formatting/currencyFormatter';
import { $t } from '@/composables/translations/useTranslate';
import {
  isPaymentScheduleReconciled,
  type PaymentSchedule,
} from '@/utils/checkout/paymentScheduleExtractor';

interface Props {
  activeStep: number;
  buttonText: string;
  totalPrice?: number;
  disabled?: boolean;
  loading?: boolean;
  hotelName?: string;
  hotelImageUrl?: string;
  dateRange?: string;
  paxLabel?: string;
  schedule?: PaymentSchedule | null;
}

const props = withDefaults(defineProps<Props>(), {
  totalPrice: undefined,
  disabled: false,
  loading: false,
  hotelName: undefined,
  hotelImageUrl: undefined,
  dateRange: undefined,
  paxLabel: undefined,
  schedule: null,
});

const emit = defineEmits<{
  'next-step': [];
  'show-details': [];
}>();

const formattedPrice = computed(() => formatPrice(props.totalPrice ?? 0, true));

const showIconRight = computed(() => props.activeStep < 2);

const showSplit = computed(
  () => props.activeStep === 2 && isPaymentScheduleReconciled(props.schedule, props.totalPrice ?? 0)
);

const phoneHref = computed(() => $t('ibe.checkout.footer.phone-link', 'tel:+4951140079900'));
</script>

<template>
  <div class="w-full">
    <div class="mb-10" :class="activeStep < 2 ? 'border-t border-black/40' : ''"></div>

    <div class="flex flex-col bg-white sm:flex-row rounded-[10px]">
      <div class="flex pb-[15px] sm:contents">
        <img
          v-if="hotelImageUrl"
          :src="hotelImageUrl"
          :alt="hotelName || 'Hotel'"
          class="object-cover self-start w-[148px] h-[148px] md:w-[176px] md:h-[176px] shrink-0 rounded-[10px]"
          loading="lazy"
        />
        <div
          v-else
          class="self-start w-[148px] h-[148px] md:w-[176px] md:h-[176px] shrink-0 rounded-[10px] bg-actions-tertiary animate-pulse"
          aria-hidden="true"
        ></div>

        <div class="flex flex-col flex-1 gap-[5px] min-w-0 px-4 pt-4 sm:px-7.5 sm:py-5">
          <p v-if="hotelName" class="text-body-large !font-semibold text-text-primary">
            {{ hotelName }}
          </p>
          <p v-if="dateRange" class="text-body">{{ dateRange }}</p>
          <p v-if="paxLabel" class="text-body">{{ paxLabel }}</p>
          <button
            type="button"
            class="text-left underline text-body w-fit"
            @click="emit('show-details')"
          >
            {{ $t('ibe.checkout.footer.all-details', 'All details') }}
          </button>
        </div>
      </div>

      <div
        class="flex flex-col items-end justify-end px-4 pb-5 shrink-0 sm:pt-[25px] sm:pl-0 sm:pr-7.5"
      >
        <p class="text-body text-right">
          {{ $t('ibe.checkout.footer.total-price', 'Total price') }}
        </p>
        <p class="text-price text-right">{{ formattedPrice }}</p>

        <template v-if="showSplit && schedule">
          <p class="text-body text-right mt-2.5">
            {{ $t('ibe.checkout.payment-schedule.deposit', 'Deposit') }}
            {{ formatPrice(schedule.deposit.amount, true) }}
          </p>
          <p class="text-body text-right">
            {{ $t('ibe.checkout.payment-schedule.balance', 'Balance') }}
            {{ formatPrice(schedule.balance.amount, true) }}
          </p>
        </template>

        <BaseButton
          :icon-right="showIconRight ? 'primary' : ''"
          :disabled="disabled"
          :loading="loading"
          variant="primary"
          type="button"
          class="mt-[15px]"
          @click="emit('next-step')"
          >{{ props.buttonText }}</BaseButton
        >
      </div>
    </div>

    <p class="flex flex-wrap justify-end gap-2.5 px-5 pt-2.5 text-body sm:px-7.5">
      <span>{{ $t('ibe.checkout.footer.questions', 'Questions?') }}</span>
      <a :href="phoneHref" class="underline">
        {{ $t('ibe.checkout.footer.phone', '+49 (0)511 400 799-0') }}
      </a>
    </p>
  </div>
</template>
